import { memo } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';

import Upload from 'src/components/upload';
import { Iconify } from 'src/components/iconify';
import { CustomPopover } from 'src/components/custom-popover';

import { FEATURE_FLAGS } from '../../global-config';
import { useMessageContext } from './hooks/uesMessageContext';

type ChatUploadProps = {
  onUpload: (files: File[]) => void;
  disabled?: boolean;
  currentFileFormat?: string;
  allowedFileTypes: string;
  isUploading?: boolean;
};

const ChatUpload = memo(function ChatUpload({
  onUpload,
  disabled,
  currentFileFormat,
  allowedFileTypes,
  isUploading,
}: ChatUploadProps) {
  const popover = usePopover();
  const { fileBrowserDialog } = useMessageContext();
  const disabledTooltipText = 'Deselect Knowledge base to enable uploads';

  // Enable menu if options are more than 1
  const totalOptions = [FEATURE_FLAGS.enableFileUpload, FEATURE_FLAGS.enableS3Integration].reduce(
    (acc, curr) => (curr === 'true' ? acc + 1 : acc),
    0
  );

  const renderDisableToolTip = (icon: string) => (
    <Tooltip title={disabledTooltipText}>
      <span>
        <IconButton disabled={disabled}>
          <Iconify width={20} icon={icon} />
        </IconButton>
      </span>
    </Tooltip>
  );

  const renderIconButton = ({
    icon,
    onClick,
    onMouseEnter,
    onMouseLeave,
    dataTestId,
    ariaLabel,
    name,
    children,
  }: {
    icon: string;
    onClick?: () => void;
    onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    dataTestId?: string;
    ariaLabel?: string;
    name?: string;
    children?: React.ReactNode;
  }) => (
    <IconButton
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      name={name}
      disabled = {isUploading}
    >
      <Iconify width={20} icon={icon} color='text.secondary' />
      {children}
    </IconButton>
  );

  const renderS3Integration = () =>
    disabled ? (
      renderDisableToolTip('logos:aws-s3')
    ) : (
      renderIconButton({
        icon: 'logos:aws-s3',
        onClick: () => {
          fileBrowserDialog.setTrue();
          popover.onClose();
        },
        onMouseEnter: popover.onOpen,
        onMouseLeave: popover.onClose,
      })
    );

  const renderFileUpload = () =>
    disabled ? (
      renderDisableToolTip('eva:attach-2-fill')
    ) : (
      <Upload
        onUpload={onUpload}
        accept={!currentFileFormat ? allowedFileTypes : currentFileFormat}
        disabled={isUploading}
      >
        {renderIconButton({
          icon: 'eva:attach-2-fill',
          dataTestId: 'upload-button',
          ariaLabel: 'upload file',
          name: 'upload file',
          onMouseEnter: popover.onOpen,
          onMouseLeave: popover.onClose,
        })}
      </Upload>
    );

  const renderAddFileMenu = () => (
    <>
      {disabled ? (
        renderDisableToolTip('eva:plus-fill')
      ) : (
        <IconButton
          sx={{
            borderRadius: 1,
          }}
          data-testid="upload-button"
          aria-label="upload file"
          name="upload file"
          onClick={popover.onOpen}
          disabled={disabled}
        >
          <Iconify width={20} icon="eva:plus-fill"  sx={{ color: 'text.secondary' }} />
        </IconButton>
      )}
      <CustomPopover
        anchorEl={popover.anchorEl}
        open={popover.open}
        onClose={popover.onClose}
        slotProps={{
          arrow: {
            placement: 'bottom-left',
          },
        }}
      >
        <Stack sx={{ p: 1 }}>
          <MenuList>
            <Upload
              onUpload={onUpload}
              accept={!currentFileFormat ? allowedFileTypes : currentFileFormat}
              disabled={disabled}
            >
              <MenuItem onClick={() => popover.onClose}>
                <Iconify width={20} icon="eva:attach-2-fill" />
                <ListItemText>
                  <Typography variant="subtitle1">Upload Files</Typography>
                </ListItemText>
              </MenuItem>
            </Upload>
            <MenuItem
              onClick={() => {
                popover.onClose();
                fileBrowserDialog.setTrue();
              }}
            >
              <Iconify width={20} icon="logos:aws-s3" />
              <ListItemText>
                <Typography variant="subtitle1">Add from S3</Typography>
              </ListItemText>
            </MenuItem>
          </MenuList>
        </Stack>
      </CustomPopover>
    </>
  );

  return (
    <>
      {totalOptions === 1 && FEATURE_FLAGS.enableFileUpload === 'true' && renderFileUpload()}
      {totalOptions === 1 && FEATURE_FLAGS.enableS3Integration === 'true' && renderS3Integration()}
      {totalOptions > 1 && renderAddFileMenu()}
    </>
  );
});

export default ChatUpload;
